import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import { IDENTIFIERS, getUrl } from '@/helpers/urlsHelper';
import { useUser } from '@/hooks';

/**
 * Validates that a URL is a safe same-origin relative path.
 * Prevents open redirect vulnerabilities by rejecting absolute URLs and protocol-relative URLs.
 */
const isSafeRelativePath = (url: string): boolean => {
  // Must start with a single forward slash (relative path)
  if (!url.startsWith('/')) {
    return false;
  }
  // Reject protocol-relative URLs (e.g., "//evil.com")
  if (url.startsWith('//')) {
    return false;
  }
  // Reject URLs with embedded protocols (e.g., "/http://evil.com" or encoded variants)
  const decodedUrl = decodeURIComponent(url);
  if (/^\/.*:\/\//i.test(decodedUrl)) {
    return false;
  }
  return true;
};

export const getNextUrl = ({
  returnTo,
  creatorSlug,
  user,
  primaryUserWorkflowId,
}: {
  creatorSlug?: string;
  returnTo?: string;
  user: { isCreator: boolean };
  primaryUserWorkflowId?: string;
}): string => {
  if (returnTo && isSafeRelativePath(returnTo)) {
    return returnTo;
  }
  if (creatorSlug) {
    if (primaryUserWorkflowId) {
      return getUrl(IDENTIFIERS.USER_PROFILE_WORKFLOW, {
        creatorSlug,
        userWorkflowId: primaryUserWorkflowId,
      });
    }
    return getUrl(IDENTIFIERS.USER_PROFILE, { creatorSlug });
  }
  if (user.isCreator) {
    return getUrl(IDENTIFIERS.DASHBOARD);
  }
  return getUrl(IDENTIFIERS.HOME);
};

const useAfterAuth = ({
  returnTo,
  creatorSlug,
  primaryUserWorkflowId,
}: {
  creatorSlug?: string;
  returnTo?: string;
  primaryUserWorkflowId?: string;
}): void => {
  const [user] = useUser();
  const router = useRouter();
  const creatorSlugRef = useRef(creatorSlug);
  creatorSlugRef.current = creatorSlug;
  const returnToRef = useRef(returnTo);
  returnToRef.current = returnTo;
  const primaryUserWorkflowIdRef = useRef(primaryUserWorkflowId);
  primaryUserWorkflowIdRef.current = primaryUserWorkflowId;

  useEffect(() => {
    if (router.isReady && user && !user.isAnonymous) {
      const url = getNextUrl({
        creatorSlug: creatorSlugRef.current,
        primaryUserWorkflowId: primaryUserWorkflowIdRef.current,
        returnTo: returnToRef.current,
        user,
      });
      router.push(url);
    }
  }, [router.isReady, user, primaryUserWorkflowId, router]);
};

export default useAfterAuth;
